import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { IconDots } from '@tabler/icons-react';
import { format } from 'date-fns';
import { findStatus } from 'lib/findStatus';
import { AgeDefault } from 'lib/DefaultData';
import toast from 'react-hot-toast';

import { formatTimeDisplay, calculateDurationMinutes } from 'lib/timeHelpers';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from 'lib/network/api';
import { API_URL } from 'lib/network/api';

const CardContentClasses = ({ data, buttonText, buttonUrl }) => {
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);

  const queryClient = useQueryClient();
  const cancelClassMutation = useMutation({
    mutationFn: async (id) => {
      return put(`${API_URL}/api/event/class/booking/${id}/cancel`);
    },
    onSuccess: () => {
      toast.success('Class canceled successfully!');
      queryClient.invalidateQueries(['myClasses']);
    },
    onError: (error) => {
      console.error('Failed to cancel class:', error);
      toast.error('Failed to cancel class');
    },
  });

  const handleCancelClass = async (id) => {
    setIsLoadingCancel(true);
    await cancelClassMutation.mutateAsync(id);
    setIsLoadingCancel(false);
  };

  if (!data?.length || data?.length === 0) {
    return (
      <div className="bg-white rounded-md h-[320px] flex justify-center items-center px-2 sm:px-0">
        <div className="flex flex-col gap-4 items-center text-center">
          <span className="leading-[normal] text-lg font-semibold">
            Looks like you haven’t signed up for any classes
          </span>
          <a
            href={buttonUrl}
            className="bg-primary-main text-white rounded-[100px] py-2 px-4 flex justify-center items-center w-full max-w-[180px]">
            <span className="text-xs font-semibold block">
              {buttonText ? buttonText : 'BOOK NOW'}
            </span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-20 gap-4 leading-[normal]">
      {data?.map((item, idx) => {
        const { start_time, end_time, start_date } = item?.class_schedule ?? {};
        const { name, duration, subject, age_group } = item?.class ?? {};
        const { name: nameClass } = item?.classroom ?? {};
        const { name: nameTeacher } = item?.teacher ?? {};
        const startTimeFormatted = formatTimeDisplay(start_time);
        const durationMinutes = calculateDurationMinutes(start_time, end_time, duration);

        const participants = item?.participants || [];

        return (
          <div className="relative" key={idx}>
            <div className="flex flex-col gap-4 text-kin-text">
              <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4">
                {/* LEFT: Schedule Info */}
                <div className="w-full sm:w-[120px] flex flex-row sm:flex-col mt-2 sm:mt-4 gap-2 sm:gap-0 justify-between sm:justify-start">
                  <span className="text-xs font-regular">
                    {start_date ? format(start_date, 'dd MMM yyyy') : ''}
                  </span>
                  <span className="text-base font-semibold">
                    {startTimeFormatted ? startTimeFormatted : '-'}
                  </span>
                  <span className="text-sm font-light">
                    {durationMinutes ? durationMinutes : '-'}
                  </span>
                </div>

                {/* RIGHT: Class Card */}

                <div className="bg-white flex flex-col font-semibold rounded-xl py-4 px-3 sm:px-6 w-full">
                  <div className="flex flex-col gap-4 sm:flex-row sm:gap-8 items-start sm:items-center flex-1 justify-between">
                    {/* CLASS INFO */}
                    <div className="flex flex-col gap-1 leading-none flex-1 w-full">
                      <span className="text-sm font-light uppercase">{subject || '-'}</span>
                      <span className="text-lg font-bold">{name || '-'}</span>
                      <span className="text-sm font-light capitalize">{nameClass}</span>
                    </div>

                    {/* CLASS META */}
                    <div className="flex flex-row gap-4 sm:gap-8 justify-between md:justify-end items-start mt-2 sm:mt-0 flex-1 w-full md:w-auto">
                      <div className="flex flex-col gap-[6px] justify-items-start leading-none">
                        <div className="flex flex-row items-center gap-2">
                          <img
                            alt="icon-star"
                            src="/kin/assest/image/icon-star.svg"
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-regular">
                            {age_group ? findStatus(age_group, AgeDefault) : ''}
                          </span>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                          <img
                            alt="icon-user"
                            src="/kin/assest/image/icon-user.svg"
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-regular">
                            {nameTeacher ? nameTeacher : '-'}
                          </span>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                          <img
                            alt="icon-clock"
                            src="/kin/assest/image/icon-calender.svg"
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-regular">
                            {durationMinutes ? durationMinutes : '-'}
                          </span>
                        </div>
                      </div>

                      {/* ACTION MENU */}
                      <Popover className="relative">
                        <PopoverButton className="p-2 bg-[#FCF2E9] rounded-md">
                          <IconDots className="w-4 h-4" />
                        </PopoverButton>
                        <PopoverPanel transition className="absolute top-[35px] right-0 w-max z-10">
                          <div>
                            <button
                              onClick={() => handleCancelClass(item.id)}
                              disabled={isLoadingCancel}
                              className="shadow-lg w-full bg-primary-main text-white rounded-lg py-2 px-3 flex justify-center items-center">
                              <span className="text-sm font-semibold block">Cancel Class</span>
                            </button>
                          </div>
                        </PopoverPanel>
                      </Popover>
                    </div>
                  </div>

                  {participants?.length > 0 && (
                    <div className="mt-4">
                      <span className="text-sm font-semibold text-kin-text mb-1 block">
                        Participants ({participants.length})
                      </span>

                      <div className="flex flex-col divide-y divide-gray-100 rounded-lg overflow-x-auto border border-gray-100 bg-white/70">
                        {participants.map((p, index) => {
                          const isChild = p.type?.toLowerCase() === 'child';
                          const isAdult = p.type?.toLowerCase() === 'adult';
                          const iconSrc = isChild
                            ? '/kin/assest/profile/children.webp'
                            : isAdult
                            ? '/kin/assest/profile/adults.webp'
                            : '/kin/assest/profile/adults.webp';

                          const badgeColor = isChild
                            ? 'bg-blue-100 text-blue-700'
                            : isAdult
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-primary-main/10 text-primary-main';

                          return (
                            <div
                              key={p.id || index}
                              className="py-3 px-2 sm:px-4 flex flex-row justify-between items-center hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-2">
                                <img src={iconSrc} alt={p.type || 'user'} className="w-8 h-8" />
                                <span className="font-medium capitalize text-sm text-kin-text truncate max-w-[120px] sm:max-w-[200px]">
                                  {p.name}
                                </span>
                              </div>

                              {/* Right: Type label */}
                              <span
                                className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${badgeColor}`}>
                                {p.type || 'member'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CardContentClasses;
